/* eslint-disable no-console */
import {Flex, Table} from 'antd';
import React, {useState} from 'react';

import BoxContent from '../../../components/BoxContent/BoxContent';
import MainTableTitle from '../../../components/Table/Utils/MainTableTitle';
import sumTableValue from '../../../components/Table/Utils/sumTableValue';
import Tabs from '../../../components/Tabs/Tabs';
import TranslatedText from '../../../libraries/I18N/TranslatedText';
import {convertPxToRem} from '../../../utils/commonFunctions';
import {formatIndian} from '../../../utils/formatIndian';
import {convertGjToTjWithUnit} from '../../../utils/formatValueWithUnit';
import {filterMainAndSubData} from '../constants/commonFunctions';
import FacilityCount from '../generalized/utils/FacilityCount';
import FacilityFilterGeneralized from '../generalized/utils/FacilityFilterGeneralized';
import {AllFacility} from '../interface/ICommon';

const AllFacilityDisplay: React.FC<{
  data: any[];
  keyArray: string[];
  showFilter?: boolean;
  type?: AllFacility;
  isSingle?: boolean;
  customTitle?: string;
  customCalKey?: string;
  displayType?: 'count' | 'flex';
  isCost?: boolean;
  convertToTJ?: boolean;
}> = ({
  data,
  keyArray,
  showFilter = true,
  type = 'generalized',
  isSingle = false,
  customTitle = undefined,
  customCalKey = '',
  displayType = 'count',
  isCost = false,
  convertToTJ = false
}) => {
  const {mainData, subData} = filterMainAndSubData(data, 'dataId', keyArray);
  const [subDataState, setSubDataState] = useState(subData ?? []);

  const columnsSwitch = (type: AllFacility) => {
    switch (type) {
      case 'generalized':
        return {
          scroll: 410,
          columns: [
            {
              title: 'Facility Name',
              dataIndex: 'facilityName'
            },
            {
              title: 'Energy Consumption',
              dataIndex: 'energyConsumption',
              render: (value: number, record: any) => {
                return (
                  <div>
                    {convertToTJ
                      ? convertGjToTjWithUnit(
                          value,
                          record?.energyConsumptionUnit
                        )
                      : `${formatIndian(
                          value
                        )} ${record?.energyConsumptionUnit}`}
                  </div>
                );
              }
            },
            {
              title: 'Floor Space',
              dataIndex: 'floorSpace',
              render: (value: number, record: any) => {
                return (
                  <div>{`${formatIndian(
                    value
                  )} ${record?.floorSpaceUnit}`}</div>
                );
              }
            },
            {
              title: 'Energy Consumption per Sq ft',
              dataIndex: 'energyConsumptionPerSqft',
              render: (value: number, record: any) => {
                return (
                  <div>
                    {convertToTJ
                      ? `${convertGjToTjWithUnit(
                          value,
                          record?.energyConsumptionUnit
                        )}/${record?.floorSpaceUnit}`
                      : `${formatIndian(
                          value
                        )} ${record?.energyConsumptionUnit}/${record?.floorSpaceUnit}`}
                  </div>
                );
              }
            }
          ]
        };
      case 'fuelUtility':
        return {
          scroll: 420,
          columns: [
            {title: 'Facility Name', dataIndex: 'facility'},
            {
              title: 'Unit (Ton)',
              dataIndex: 'value',
              render: (value: number) => {
                return formatIndian(value);
              }
            }
          ]
        };
      case 'electricCost':
        return {
          scroll: 280,
          columns: [
            {title: 'Facility Name', dataIndex: 'facility'},
            {
              title: 'Cost (₹)',
              dataIndex: 'value',
              render: (value: number) => {
                return formatIndian(value);
              }
            }
          ]
        };
      case 'electricFacility':
        return {
          scroll: 0,
          columns: [
            {title: 'Facility Name', dataIndex: 'facility'},
            {
              title: 'Cost',
              dataIndex: 'value',
              render: (value: number) => {
                return formatIndian(value);
              }
            }
          ]
        };
      case 'electricCon':
        return {
          scroll: 270,
          columns: [
            {title: 'Facility Name', dataIndex: 'facility'},
            {
              title: 'Unit (kWh)',
              dataIndex: 'value',
              render: (value: any) => {
                return formatIndian(value);
              }
            }
          ]
        };
    }
  };

  const {columns, scroll} = columnsSwitch(type);

  const getLeasedData = (isLeased: boolean) => {
    return (
      subDataState
        ?.map((item, index) => {
          return {...item, key: index};
        })
        .filter((item) => {
          return item?.isLeased === isLeased;
        }) ?? []
    );
  };

  const leased = getLeasedData(true);
  const nonLeased = getLeasedData(false);

  const tabArray = [
    {
      id: 'leased',
      name: <TranslatedText translationKey="Leased Space" />,
      component: (
        <Table
          dataSource={leased.map((item, index) => {
            return {...item, key: index};
          })}
          columns={columns}
          scroll={{y: scroll}}
          rowKey="key"
          pagination={false}
        />
      )
    },
    {
      id: 'Non-leased',
      name: <TranslatedText translationKey="Non Leased Space" />,
      component: (
        <Table
          dataSource={nonLeased.map((item, index) => {
            return {...item, key: index};
          })}
          columns={columns}
          scroll={{y: scroll}}
          rowKey="key"
          pagination={false}
        />
      )
    }
  ];

  return (
    <BoxContent>
      <Flex vertical={true} gap="1rem">
        <MainTableTitle
          title={
            data?.find((item) => {
              return item?.dataId === keyArray[0];
            })?.label ?? 'All Facility'
          }
          showDivider={false}
        />
        {showFilter && (
          <FacilityFilterGeneralized
            setMainData={setSubDataState}
            subData={subData}
          />
        )}
        {displayType === 'count' ? (
          <FacilityCount
            subData={subDataState}
            leased={leased}
            nonLeased={nonLeased}
            isSingle={isSingle}
            customTitle={customTitle}
            mainData={mainData}
            customCalKey={customCalKey}
            isCost={isCost}
            convertToTJ={convertToTJ}
          />
        ) : (
          <Flex
            gap="1rem"
            style={{
              padding: '0.5rem',
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: 'flex-start'
            }}
          >
            <img
              src={mainData?.image ?? ''}
              style={{width: convertPxToRem(63), height: convertPxToRem(63)}}
            />
            <Flex vertical={true}>
              <div
                style={{
                  font: 'normal normal 600 12px/15px Montserrat',
                  color: '#99B1C7'
                }}
              >
                {customTitle !== '' ? customTitle : 'TOTAL NO. OF FRANCHISEES'}
              </div>
              <div
                style={{
                  font: 'normal normal 600 24px/29px Montserrat',
                  color: '#677489'
                }}
              >
                {customCalKey === ''
                  ? subData?.length ?? 0
                  : sumTableValue(subData, customCalKey)}
                <span>
                  {mainData?.unit ? ` ${mainData?.unit ?? ''}` : ' No.'}
                </span>
              </div>
            </Flex>
          </Flex>
        )}
        <Tabs array={tabArray} noPadding type="card" />
      </Flex>
    </BoxContent>
  );
};

export default AllFacilityDisplay;
